import { createClient } from './http';
import type {
  AuditEvent,
  ChangePasswordRequest,
  CreateWorkspaceRequest,
  Group,
  LoginRequest,
  LoginResponse,
  Me,
  MfaVerifyRequest,
  MfaVerifyResponse,
  Organization,
  OrganizationMember,
  OrganizationUpdateRequest,
  RegisterRequest,
  RegisterResponse,
  RolesOverview,
  SessionSummary,
  TokenPair,
  TotpSetupResponse,
  UpdateProfileRequest,
  VerifyCodeRequest,
  WorkspaceSummary,
} from './types';

export { ApiError } from './http';

export const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/auth';

const request = createClient(AUTH_API_BASE);

export const api = {
  yandexTokenLogin: (accessToken: string) =>
    request<TokenPair>('/auth/oauth/yandex/token', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken }),
    }),

  register: (body: RegisterRequest) =>
    request<RegisterResponse>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),

  verify: (body: VerifyCodeRequest) =>
    request<MfaVerifyResponse>('/auth/verify', { method: 'POST', body: JSON.stringify(body) }),

  resendCode: (challengeId: string) =>
    request<{ challenge_id: string; expires_at: string }>('/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ challenge_id: challengeId }),
    }),

  login: (body: LoginRequest) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),

  mfaVerify: (body: MfaVerifyRequest) =>
    request<MfaVerifyResponse>('/auth/mfa/verify', { method: 'POST', body: JSON.stringify(body) }),

  passwordResetRequest: (email: string) =>
    request<{ status: string }>('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  passwordResetConfirm: (token: string, newPassword: string) =>
    request<{ status: string }>('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    }),

  listWorkspaces: () => request<WorkspaceSummary[]>('/workspaces', {}, true),

  createWorkspace: (body: CreateWorkspaceRequest) =>
    request<WorkspaceSummary>('/workspaces', { method: 'POST', body: JSON.stringify(body) }, true),

  selectWorkspace: (workspaceId: string) =>
    request<{ tokens: { access_token: string; refresh_token: string }; workspace: WorkspaceSummary }>(
      '/workspaces/select',
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) },
      true,
    ),

  deleteWorkspace: (workspaceId: string) =>
    request<void>(`/workspaces/${workspaceId}`, { method: 'DELETE' }, true),

  telegramLinkToken: () =>
    request<{ link_url: string; expires_at: string }>('/telegram/link-token', { method: 'POST' }, true),

  getMe: () => request<Me>('/me', {}, true),

  updateMe: (body: UpdateProfileRequest) =>
    request<Me>('/me', { method: 'PATCH', body: JSON.stringify(body) }, true),

  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<Me>('/me/avatar', { method: 'POST', body: formData }, true);
  },

  getPreferences: () => request<{ preferences: Record<string, unknown> }>('/me/preferences', {}, true),

  updatePreferences: (preferences: Record<string, unknown>) =>
    request<{ status: string }>(
      '/me/preferences',
      { method: 'PATCH', body: JSON.stringify({ preferences }) },
      true,
    ),

  changePassword: (body: ChangePasswordRequest) =>
    request<{ status: string }>('/me/password', { method: 'POST', body: JSON.stringify(body) }, true),

  listSessions: () => request<SessionSummary[]>('/me/sessions', {}, true),

  revokeSession: (sessionId: string) =>
    request<{ status: string }>(`/me/sessions/${sessionId}`, { method: 'DELETE' }, true),

  setupTotp: () => request<TotpSetupResponse>('/me/mfa/totp/setup', { method: 'POST' }, true),

  confirmTotp: (code: string) =>
    request<{ status: string }>(
      '/me/mfa/totp/confirm',
      { method: 'POST', body: JSON.stringify({ code }) },
      true,
    ),

  disableTotp: () => request<{ status: string }>('/me/mfa/totp', { method: 'DELETE' }, true),

  getOrganization: () => request<Organization>('/organizations/current', {}, true),

  updateOrganization: (body: OrganizationUpdateRequest) =>
    request<Organization>('/organizations/current', { method: 'PATCH', body: JSON.stringify(body) }, true),

  listOrganizationMembers: () =>
    request<OrganizationMember[]>('/organizations/current/members', {}, true),

  inviteOrganizationMember: (email: string) =>
    request<OrganizationMember>(
      '/organizations/current/invites',
      { method: 'POST', body: JSON.stringify({ email }) },
      true,
    ),

  setOrganizationMemberAdmin: (memberId: string, isAdmin: boolean) =>
    request<OrganizationMember>(
      `/organizations/current/members/${memberId}`,
      { method: 'PATCH', body: JSON.stringify({ is_admin: isAdmin }) },
      true,
    ),

  removeOrganizationMember: (memberId: string) =>
    request<void>(`/organizations/current/members/${memberId}`, { method: 'DELETE' }, true),

  getRolesOverview: () => request<RolesOverview>('/organizations/current/roles-overview', {}, true),

  getAuditLog: (
    params: {
      limit?: number;
      offset?: number;
      actor_user_id?: string;
      date_from?: string;
      date_to?: string;
      sort?: 'asc' | 'desc';
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.actor_user_id) query.set('actor_user_id', params.actor_user_id);
    if (params.date_from) query.set('date_from', params.date_from);
    if (params.date_to) query.set('date_to', params.date_to);
    if (params.sort) query.set('sort', params.sort);
    const qs = query.toString();
    return request<AuditEvent[]>(`/organizations/current/audit-log${qs ? `?${qs}` : ''}`, {}, true);
  },

  logout: (refreshToken: string) =>
    request<{ status: string }>('/auth/logout', { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) }, true),

  listGroups: () => request<Group[]>('/organizations/current/groups', {}, true),

  createGroup: (name: string) =>
    request<Group>('/organizations/current/groups', { method: 'POST', body: JSON.stringify({ name }) }, true),

  renameGroup: (groupId: string, name: string) =>
    request<Group>(
      `/organizations/current/groups/${groupId}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
      true,
    ),

  deleteGroup: (groupId: string) =>
    request<void>(`/organizations/current/groups/${groupId}`, { method: 'DELETE' }, true),

  addGroupMember: (groupId: string, userId: string) =>
    request<Group>(
      `/organizations/current/groups/${groupId}/members`,
      { method: 'POST', body: JSON.stringify({ user_id: userId }) },
      true,
    ),

  removeGroupMember: (groupId: string, userId: string) =>
    request<Group>(`/organizations/current/groups/${groupId}/members/${userId}`, { method: 'DELETE' }, true),

  assignWorkspaceMember: (workspaceId: string, userId: string, roleCode: string) =>
    request<WorkspaceSummary>(
      `/workspaces/${workspaceId}/members`,
      { method: 'POST', body: JSON.stringify({ user_id: userId, role_code: roleCode }) },
      true,
    ),

  updateWorkspaceMemberRole: (workspaceId: string, userId: string, roleCode: string) =>
    request<WorkspaceSummary>(
      `/workspaces/${workspaceId}/members/${userId}`,
      { method: 'PATCH', body: JSON.stringify({ role_code: roleCode }) },
      true,
    ),

  removeWorkspaceMember: (workspaceId: string, userId: string) =>
    request<void>(`/workspaces/${workspaceId}/members/${userId}`, { method: 'DELETE' }, true),

  grantWorkspaceGroup: (workspaceId: string, groupId: string, roleCode: string) =>
    request<{ status: string }>(
      `/workspaces/${workspaceId}/groups`,
      { method: 'POST', body: JSON.stringify({ group_id: groupId, role_code: roleCode }) },
      true,
    ),

  revokeWorkspaceGroup: (workspaceId: string, groupId: string) =>
    request<void>(`/workspaces/${workspaceId}/groups/${groupId}`, { method: 'DELETE' }, true),
};
